import { CompareFn } from '@epicuro-next/models/sorting';
import { compareNumbers, compareStrings } from '@epicuro-next/utilities';
import {
  BasketColumnKeys,
  BasketGroupSortType,
  BasketRow,
  SortDirection,
} from 'graphql-api/baskets-api-interfaces';

import { getLineColumnValues } from '../shared';

/**
 * Default comparing method uses WSP.
 */
export function compareByTotalWsp(
  a: BasketRow,
  b: BasketRow,
  sortDirection: SortDirection,
) {
  return compareNumbers<BasketRow>(
    (x) => x.total.requested.wspd ?? x.total.requested.wsp,
  )(a, b, sortDirection);
}

function deliverySelector(row: BasketRow) {
  const { requestedDeliveryDate, consignmentStartDate } = row.delivery;

  if (!requestedDeliveryDate) {
    return row.basketItems
      .map((x) =>
        getTimeString(x.consignmentStartDate ?? x.requestedDeliveryDate),
      )
      .sort((a, b) => a - b)[0];
  }

  return getTimeString(consignmentStartDate ?? requestedDeliveryDate);
}

function getTimeString(value: string) {
  return new Date(value).getTime();
}

/**
 * Comparers configuration.
 */
export const comparers: Record<
  BasketGroupSortType,
  CompareFn<BasketRow>
> = Object.freeze({
  [BasketGroupSortType.TotalWsp]: compareNumbers(
    (x) => getLineColumnValues(x, BasketColumnKeys.Value) ?? 0,
  ),
  [BasketGroupSortType.TotalNet]: compareNumbers(
    (x) => getLineColumnValues(x, BasketColumnKeys.ValueNet) ?? 0,
  ),
  [BasketGroupSortType.TotalRrp]: compareNumbers(
    (x) => getLineColumnValues(x, BasketColumnKeys.ValueRrp) ?? 0,
  ),
  [BasketGroupSortType.Quantity]: compareNumbers(
    (x) => getLineColumnValues(x, BasketColumnKeys.Quantity) ?? 0,
  ),
  [BasketGroupSortType.Wsp]: compareNumbers(
    (x) => getLineColumnValues(x, BasketColumnKeys.Wsp) ?? 0,
  ),
  [BasketGroupSortType.Rrp]: compareNumbers(
    (x) => getLineColumnValues(x, BasketColumnKeys.Rrp) ?? 0,
  ),
  [BasketGroupSortType.Markup]: compareNumbers(
    (x) => getLineColumnValues(x, BasketColumnKeys.Markup) ?? 0,
  ),
  [BasketGroupSortType.Delivery]: compareNumbers(deliverySelector),
  [BasketGroupSortType.CreatedDate]: compareStrings((x) => {
    const created = x.created || '';
    // used for sorting items added at same time
    const productId = x.product?.productId ?? '';
    return `${created}-${productId}`;
  }),
  [BasketGroupSortType.Alphabetical]: compareStrings(
    (x) => x.product.title || '',
  ),
  [BasketGroupSortType.ProductId]: compareStrings(
    (x) => x.product.productId || '',
  ),
  [BasketGroupSortType.Gender]: compareStrings((x) => x.product.gender || ''),
});
